import type { RichTextField } from "@prismicio/client"
import classNames from "classnames"
import React from "react"

import { DateTime } from "@bounce/date"
import { Trans } from "@bounce/i18n"
import type { Nullable } from "@bounce/util"

import type { Interpolations } from "../../../../libs/prismic/helpers"
import { RichText } from "../RichText/RichText"

export type RichTextSectionProps = {
  className?: string
  field: Nullable<RichTextField>
  interpolations?: Interpolations | undefined
  publicationDate?: Nullable<string>
  disableImgBlueFilter?: boolean
  hidePublicationDate?: boolean
} & React.HTMLAttributes<HTMLElement>

export const RichTextSection = ({
  field,
  interpolations,
  publicationDate,
  className,
  disableImgBlueFilter = false,
  hidePublicationDate = false,
  ...sectionProps
}: RichTextSectionProps) => {
  if (!field) return null

  const lastUpdate = publicationDate ? DateTime.fromISO(publicationDate) : DateTime.now()
  const lastUpdateFormattedDate = lastUpdate.toLocaleStringGlobal(DateTime.DATE_MED)
  const lastUpdateDate = lastUpdate.toISODate()

  return (
    <section className={classNames("mx-auto flex w-full max-w-screen-xl flex-col px-6", className)} {...sectionProps}>
      {!hidePublicationDate && (
        <span className="mb-1 mt-0 text-xs text-neutral-800 md:text-sm" data-chromatic="ignore">
          <Trans
            values={{ lastUpdateFormattedDate }}
            i18nKey="cmp.richTextSection.postedDate"
            defaults="Posted <time>{{lastUpdateFormattedDate}}</time>"
            components={{
              time: <time dateTime={lastUpdateDate} data-chromatic="ignore" suppressHydrationWarning />,
            }}
          />
        </span>
      )}
      <RichText
        field={field}
        interpolations={interpolations}
        disableImgBlueFilter={disableImgBlueFilter}
        className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
      />
    </section>
  )
}
